import type { Balance } from '@domain/entities/Balance';

export interface BalanceLine {
  readonly label: string;
  readonly value: number;
  readonly note: string;
}

/**
 * Las líneas del saldo en la ficha, de arriba abajo.
 *
 * **Lo descontado al abrir el periodo se parte en dos cuando hace falta.** Viaja
 * en un solo campo —`openingUsedDays`— porque se descuenta igual, pero son dos
 * cosas: días gastados antes de que existiera el portal, y la **deuda** de
 * haberse pasado en el periodo anterior (`carriedDebtDays`). La ficha llamaba a
 * las dos «Ya gastados fuera del portal», y el 21 sep 2026 eso hizo creer que a
 * cinco personas se les había copiado una carga vieja al periodo nuevo; era la
 * deuda. Cada una sale con su nombre, y la de fuera del portal sólo si hay algo.
 */
export const balanceLinesFor = (params: {
  readonly balance: Balance;
  /** El derecho que se está previsualizando, con el de contrato ya aplicado. */
  readonly entitled: number;
  /** Lo que hay escrito en el campo de ajuste; el total descontado al abrir. */
  readonly opening: number;
}): readonly BalanceLine[] => {
  const { balance, entitled, opening } = params;
  const deuda = Math.min(balance.carriedDebtDays, opening);
  const fueraDelPortal = opening - deuda;

  return [
    { label: 'Derecho del periodo', value: entitled, note: 'Por antigüedad o por contrato' },
    ...(deuda > 0
      ? [
          {
            label: 'Deuda del periodo anterior',
            value: deuda,
            note: `Días que tomó de más en su periodo ${String(balance.year - 1)}`,
          },
        ]
      : []),
    ...(deuda === 0 || fueraDelPortal > 0
      ? [
          {
            label: 'Ya gastados fuera del portal',
            value: fueraDelPortal,
            note: 'Lo que traía al importarse',
          },
        ]
      : []),
    { label: 'Tomados', value: balance.takenDays, note: 'Días que ya ocurrieron' },
    { label: 'Agendados', value: balance.scheduledDays, note: 'Aprobados y todavía por venir' },
    { label: 'Pendientes', value: balance.pendingDays, note: 'Enviados y sin decidir' },
  ];
};
